import { Link } from '@tanstack/react-router'

import EmptyState, { type EmptyStateProps } from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import { upgradeTitle } from '@/features/subscriptions/lib/upgradeToast'

interface UpgradeRequiredProps {
  /** The 403's `fields.feature` (CONTRACT §7) — never `required_tier` or `message`. */
  featureKey: string
  size?: EmptyStateProps['size']
  framed?: boolean
  className?: string
}

/**
 * The dormant 403 `SUBSCRIPTION_REQUIRED` state for a gated READ (CONTRACT
 * §7, trap 11). Built on `EmptyState` like every other "nothing to show"
 * block — never the 403's own `message` ("This requires the Bull plan." can
 * tell a Guru member they need Bull — `required_tier` is a hard-coded "bull"
 * fallback on every feature gate, not the plan that actually grants it) or
 * `fields.required_tier`. The only trustworthy part of the body is
 * `fields.feature`, read by `subscriptionRequiredFeature` — see
 * `lib/paywall.ts`.
 *
 * Nothing renders this today: `enforced` is `false` locally and on dev
 * (CONTRACT §7's observe/enforce table), so every gated endpoint still
 * answers 200. It exists so the day mm-be flips to `enforce`, the screens
 * that call `subscriptionRequiredFeature(error)` already have somewhere to
 * land — see the forced-403 harness runs in the build report.
 */
export default function UpgradeRequired({
  featureKey,
  size = 'page',
  framed,
  className,
}: UpgradeRequiredProps) {
  return (
    <EmptyState
      kind="not-available"
      size={size}
      framed={framed}
      className={className}
      title={upgradeTitle(featureKey)}
      description="Your current plan doesn't include it. See which plans do."
      action={
        <Link to="/subscription" className={stateActionClass(size)}>
          See plans
        </Link>
      }
    />
  )
}
